import type { AABB2 } from "geom/aabb2Types";

export const DRAWING_LAYOUT = "DRAWING_LAYOUT";

export interface DrawingLayout {
  readonly drawingLayoutType: typeof DRAWING_LAYOUT; // This is here just to disambiguate this from LayoutSettings at runtime.
  readonly type: LayoutMethodType | undefined;
  readonly pageWidth: number;
  readonly pageHeight: number;
  readonly dwgCenterX: number;
  readonly dwgCenterY: number;
  readonly pageCenterX: number;
  readonly pageCenterY: number;
  readonly scaleFactor: number;
  readonly bb: Readonly<AABB2> | undefined;
}

/**
 * LayoutSettings determines a policy for how drawings can share scale information.
 * This can be useful when drawings are to be overlaid on top of each other.
 */
export interface LayoutSettings {
  readonly shareType?: LayoutShareType;
  readonly layoutType?: LayoutMethod;
  readonly layoutKey?: string;
}

export enum LayoutMethodType {
  ZoomToFit,
  ScaleAroundCentroid,
  ZoomToFitJustifyTopLeft,
  ZoomToFitJustifyTopRight,
  ZoomToFitJustifyTopCenter,
  Static,
}

interface ZoomToFitJustifyTopLeft {
  readonly layoutMethod: LayoutMethodType.ZoomToFitJustifyTopLeft;
}

interface ZoomToFitJustifyTopRight {
  readonly layoutMethod: LayoutMethodType.ZoomToFitJustifyTopRight;
}
interface ZoomToFitJustifyTopCenter {
  readonly layoutMethod: LayoutMethodType.ZoomToFitJustifyTopCenter;
}

export interface ZoomToFit {
  readonly layoutMethod: LayoutMethodType.ZoomToFit;
  readonly margin?: number; // Note: Non-zero by default!
}

interface ScaleAroundCentroid {
  readonly layoutMethod: LayoutMethodType.ScaleAroundCentroid;
  readonly scale: number;
}

interface Static {
  readonly layoutMethod: LayoutMethodType.Static;
  readonly scale: number;
}

export type LayoutMethod =
  | ZoomToFit
  | ScaleAroundCentroid
  | ZoomToFitJustifyTopLeft
  | ZoomToFitJustifyTopRight
  | ZoomToFitJustifyTopCenter
  | Static;

export enum LayoutShareType {
  None,
  Read,
  Write,
  ReadScaleOnly,
}

export function isDrawingLayout(layout: any): layout is DrawingLayout {
  return layout && layout.drawingLayoutType === DRAWING_LAYOUT;
}

/**
 * LayoutMap works along with LayoutSettings in order to enable drawings to share layout
 * information between each other. This is useful when drawings are to be overlaid on top
 * of each other.
 *
 * LayoutMap's typically are passed into the final stage of plan generation and the SheetNode
 * tree is traversed. The selectLayout function is called on each SheetNode and it
 * is responsible for determining which layout to use for that SheetNode.
 */
export type LayoutMap = { [key: string]: DrawingLayout };

export function layoutMapNew(): LayoutMap {
  return {};
}
